import { Store } from '@ngrx/store';
import { Injectable } from '@angular/core';
import { createAsyncWarning, createWarning } from '@core/helpers/create-validator.helper';
import { isAfter, isBefore } from 'date-fns';
import { Revisione53State } from '../../../store/revisione53.reducers';
import { Revisione53DatiBusinessSelectors } from '../../../store/selectors/dati-business.selectors';
import { filter, map, take } from 'rxjs';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { toNum } from '@shared/helpers/math.helper';
import { MyElSocietaPartecipate } from '../../../models/interfaces/revisione.interface';

@Injectable()
export class DatiBilancioWarningsService {
    constructor(private readonly store: Store<Revisione53State>) {}

    my_CkSuddivCapitSoc = createWarning(this.store, (myVerbale) => {
        const my_CkSuddivCapitSoc = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_CkSuddivCapitSoc;
        const my_ValoreNomSuddivCapit = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_ValoreNomSuddivCapit;

        if (!(my_CkSuddivCapitSoc !== '2' || toNum(my_ValoreNomSuddivCapit) <= 719)) {
            return 'Attenzione, Verificare valore dell’azione';
        }
    });

    my_CkEnteTenutoCertBil = createWarning(this.store, (myVerbale) => {
        const my_CkEnteTenutoCertBil = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_CkEnteTenutoCertBil;

        if (my_CkEnteTenutoCertBil === '1') {
            return 'La cooperativa certificabile è revisionata annualmente';
        }
    });

    percentualeDaVerificare = createWarning(this.store, (myVerbale) => {
        const basePath = myVerbale.my_SezioneI__Rilevazione.my_NaturaMutualistica.my_SchedaPrevalenza;
        const isNotOne = (v: string) => v !== '1';
        const ckOffreBeniServ = basePath.my_CkOffreBeniServCoopAtt;
        const ckOffreOccLav = basePath.my_CkOffreOccLavSociCoopAtt;
        const ckRemunConferProd = basePath.my_CkRemunConferProdSociCoopAtt;
        const ckRemunPrestConfServ = basePath.my_CkRemunPrestConfServSociCoopAtt;
        const groupA = isNotOne(ckRemunConferProd) && isNotOne(ckOffreBeniServ);
        const groupB = isNotOne(ckRemunPrestConfServ) && isNotOne(ckRemunConferProd);
        const hitA = groupA && (isNotOne(ckOffreOccLav) || isNotOne(ckRemunPrestConfServ));
        const hitB = groupB && (isNotOne(ckOffreBeniServ) || isNotOne(ckOffreOccLav));

        if (!(hitA || hitB)) {
            return 'Percentuale da verificare';
        }

        return undefined;
    });

    my_PercSpaSrl = createWarning(this.store, (myVerbale) => {
        const elements = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_SocietaPartecipate.my_ElSocietaPartecipate;
        const hasMatch = (elements ?? []).some((el: MyElSocietaPartecipate) => el.my_TipoSocietaPartec === '2' && toNum(el.my_PercSpaSrl) > 50);
        return hasMatch ? 'La cooperativa è revisionata annualmente.' : null;
    });

    my_CkRipettatoCICR = createWarning(this.store, (myVerbale) => {
        const my_Bilancio = myVerbale.my_SezioneI__Rilevazione.my_Bilancio;
        const my_CICR = my_Bilancio.my_SchedaVerificaPrestito.my_CICR;
        const my_NumSociDataDich = my_Bilancio.my_SchedaVerificaPrestito.my_NumSociDataDich;
        const my_CkPrestitoSoci = my_Bilancio.my_CkPrestitoSoci;

        if (toNum(my_CICR) > 3 && toNum(my_NumSociDataDich) > 50 && my_CkPrestitoSoci === '1') {
            return 'Un valore del rapporto CICR maggiore di 3 è anomalo';
        }
    });

    my_TotConfSociFin = createWarning(this.store, (myVerbale) => {
        const my_TotConfSociFin = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_TotConfSociFin;

        if (!isNilOrEmpty(my_TotConfSociFin) && my_TotConfSociFin > '1549370,69') {
            return 'Il totale conferito dai soci finanziatori è superiore a euro 1.549.370,69 Pertanto la cooperativa è revisionabile annualmente';
        }
    });

    my_BilAnno2 = createWarning(this.store, (myVerbale) => {
        const my_BilAnno2 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_BilAnno2;
        const my_AnnoUltimoBilanciNelDbCentrale = myVerbale.my_AnnoUltimoBilanciNelDBCentrale;

        if (!(isNilOrEmpty(my_BilAnno2) || my_BilAnno2 != my_AnnoUltimoBilanciNelDbCentrale)) {
            return 'Attenzione è stato allegato un bilancio di esercizio oggetto di precedente revisione';
        }
    });

    my_ConfSociFin = createWarning(this.store, (myVerbale) => {
        const my_TotConfSociFin = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_TotConfSociFin;
        const my_CapSocSottoscrSecondoEs = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_CapSocSottoscrSecondoEs;

        if (!(isNilOrEmpty(my_TotConfSociFin) || my_TotConfSociFin <= my_CapSocSottoscrSecondoEs)) {
            return 'Soci Finanziatori: Il conferimento dei soci finanziatori è maggiora del capitale sottoscritto ';
        }
    });

    my_AnnoUltimoBilanciNelDBCentrale = createWarning(this.store, (myVerbale) => {
        const my_AnnoUltimoBilanciNelDBCentrale = myVerbale.my_AnnoUltimoBilanciNelDBCentrale;
        const my_BilAnno1 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_BilAnno1;
        const my_CkEnteTenutoCertbil = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_CkEnteTenutoCertBil;
        const my_ElCertificazioniPassate = myVerbale.my_CertificazioniPassate.my_ElCertificazioniPassate;

        if (!(my_AnnoUltimoBilanciNelDBCentrale >= my_BilAnno1 || my_CkEnteTenutoCertbil != '0' || my_ElCertificazioniPassate?.length === 0)) {
            return 'Si invita il revisore a verificare anche in bilancio, pur rispondendo NO alla presente domanda';
        }
    });

    my_ImportoPatrSecondoEs = createWarning(this.store, (myVerbale) => {
        const my_ImportoPatrSecondoEs = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_ImportoPatrSecondoEs;
        const my_DataChiusura2 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_DataChiusura2;
        const date = new Date(my_DataChiusura2);
        const isInCovidRange = !isBefore(date, new Date('09/04/2020')) && !isAfter(date, new Date('31/12/2022'));

        if (!(isNilOrEmpty(my_ImportoPatrSecondoEs) || Number(my_ImportoPatrSecondoEs) >= 0 || isInCovidRange)) {
            return 'Attenzione, il patrimonio netto è negativo';
        }
    });

    my_ImportoPatrSecondoEs2 = createWarning(this.store, (myVerbale) => {
        const my_ImportoPatrSecondoEs = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_ImportoPatrSecondoEs;
        const my_DataChiusura2 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_DataChiusura2;
        const date = new Date(my_DataChiusura2);
        const isBeforeCheck = isBefore(date, new Date('09/04/2020'));
        const isAfterCheck = isAfter(date, new Date('31/12/2022'));

        if (!(Number(my_ImportoPatrSecondoEs) >= 0 || isNilOrEmpty(my_ImportoPatrSecondoEs) || isAfterCheck || isBeforeCheck)) {
            return 'Attenzione, si valuti il patrimonio netto negativo insieme a quanto stabilito da art 6 D.L. 8 Aprile 2020 n.3';
        }
    });

    my_CapSocVersatoSecondoEs = createWarning(this.store, (myVerbale) => {
        const my_CapSocVersatoSecondoEs = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_CapSocVersatoSecondoEs;
        const my_CapSocSottoscrSecondoEs = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_CapSocSottoscrSecondoEs;

        if (!(toNum(my_CapSocVersatoSecondoEs) >= toNum(my_CapSocSottoscrSecondoEs) || isNilOrEmpty(my_CapSocSottoscrSecondoEs) || isNilOrEmpty(my_CapSocVersatoSecondoEs))) {
            return 'Attenzione: verificare le condizioni del capitale sociale eventualmente suggerire alla cooperativa il versamento';
        }
    });

    my_ImportoPatrSecondoEs3 = createWarning(this.store, (myVerbale) => {
        const my_ImportoPatrSecondoEs = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_ImportoPatrSecondoEs;
        const my_CapSocVersatoSecondoEs = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_CapSocVersatoSecondoEs;
        const my_CapSocSottoscrSecondoEs = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_CapSocSottoscrSecondoEs;

        if (
            !(
                Number(my_ImportoPatrSecondoEs) < 0 ||
                Number(my_ImportoPatrSecondoEs) - (Number(my_CapSocSottoscrSecondoEs) - Number(my_CapSocVersatoSecondoEs)) > 0 ||
                isNilOrEmpty(my_CapSocSottoscrSecondoEs) ||
                isNilOrEmpty(my_CapSocVersatoSecondoEs) ||
                isNilOrEmpty(my_ImportoPatrSecondoEs)
            )
        ) {
            return 'Attenzione, a meno dei versamenti docuti dai soci, il patrimonio è negativo ';
        }
    });

    my_NumBilanciInExcelSezI = createWarning(this.store, (myVerbale) => {
        const my_NumBilanciInExcel = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_NumBilanciInExcel;

        if (my_NumBilanciInExcel === '1') {
            return 'Si ricorda al revisore che è possibile inserire un solo bilancio nel file. Per fare ciò è sufficiente non indicare nulla nella colonna D (Primo bilancio). In particolare si raccomanda di non inserire la data di approvazione, il tipo di schema ed il tipo di bilancio in suddetta colonna, così come non dovranno essere indicati valori nello stato patrimoniale e nel conto economico.';
        }
    });

    my_NumBilanciInExcelSezII = createWarning(this.store, (myVerbale) => {
        const my_NumBilanciInExcel = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_NumBilanciInExcel;

        /* if (my_NumBilanciInExcel === '0') {
      return "Per redigere un verbale informatico senza allegare alcun bilancio è necessario inserire nel file SOLTANTO il giorno e mese di chiusura dell' esercizio e indicare 1900 come anno del bilancio.";
    } */
        return null;
    });

    my_BilancioAllegato = createWarning(this.store, (myVerbale) => {
        const my_BilancioAllegato = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_BilancioAllegato;

        if (isNilOrEmpty(my_BilancioAllegato)) {
            return 'Allegare un bilancio';
        }
    });

    my_ChiusuraEsercizio = createWarning(this.store, (myVerbale) => {
        const my_ChiusuraEsercizio = myVerbale.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_ChiusuraEsercizio;
        const my_BilancioAllegato = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_BilancioAllegato;

        if (!(!isNilOrEmpty(my_ChiusuraEsercizio) || isNilOrEmpty(my_BilancioAllegato))) {
            return 'Specificare gg-mm di chiusura esercizio';
        }
    });

    my_BilAnno1 = createWarning(this.store, (myVerbale) => {
        const my_BilAnno1 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_BilAnno1;
        const my_BilancioAllegato = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_BilancioAllegato;

        if (!(!isNilOrEmpty(my_BilAnno1) || isNilOrEmpty(my_BilancioAllegato))) {
            return "Specificare l'anno del 1° bilancio";
        }
    });

    my_BilAnno2II = createWarning(this.store, (myVerbale) => {
        const my_BilAnno2 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_BilAnno2;
        const my_BilancioAllegato = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_BilancioAllegato;

        if (!(!isNilOrEmpty(my_BilAnno2) || isNilOrEmpty(my_BilancioAllegato))) {
            return "Specificare l'anno del 2° bilancio";
        }
    });

    my_DataApprovazione1 = createWarning(this.store, (myVerbale) => {
        const my_DataApprovazione1 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_DataApprovazione1;
        const my_BilancioAllegato = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_BilancioAllegato;
        const my_TipoSchemaBil1 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_TipoSchemaBil1;

        if (!(isNilOrEmpty(my_BilancioAllegato) || !isNilOrEmpty(my_DataApprovazione1) || isNilOrEmpty(my_TipoSchemaBil1))) {
            return 'Specificare la data di approvazione del 1° bilancio';
        }
    });

    my_TipoSchemaBil2 = createWarning(this.store, (myVerbale) => {
        const my_DataApprovazione2 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_DataApprovazione2;
        const my_BilancioAllegato = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_BilancioAllegato;

        if (!(isNilOrEmpty(my_BilancioAllegato) || !isNilOrEmpty(my_DataApprovazione2))) {
            return 'Specificare la data di approvazione del 2° bilancio';
        }
    });

    my_TipoBilancioAbbr1 = createWarning(this.store, (myVerbale) => {
        const my_TipoSchemaBil1 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_TipoSchemaBil1;
        const my_BilancioAllegato = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_BilancioAllegato;
        const my_DataApprovazione1 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_DataApprovazione1;

        if (!(!isNilOrEmpty(my_TipoSchemaBil1) || isNilOrEmpty(my_DataApprovazione1) || isNilOrEmpty(my_BilancioAllegato))) {
            return 'Specificare il tipo schema del 1° bilancio';
        }
    });

    my_TipoBilancioAbbr2 = createWarning(this.store, (myVerbale) => {
        const my_TipoSchemaBil2 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_TipoSchemaBil2;
        const my_BilancioAllegato = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_BilancioAllegato;

        if (!(!isNilOrEmpty(my_TipoSchemaBil2) || isNilOrEmpty(my_BilancioAllegato))) {
            return 'Specificare il tipo schema del 2° bilancio';
        }
    });

    my_TipoBilancioAbbr1II = createWarning(this.store, (myVerbale) => {
        const my_TipoBilancioAbbr1 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_TipoBilancioAbbr1;
        const my_BilancioAllegato = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_BilancioAllegato;
        const my_DataApprovazione1 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_DataApprovazione1;

        if (!(!isNilOrEmpty(my_TipoBilancioAbbr1) || isNilOrEmpty(my_DataApprovazione1) || isNilOrEmpty(my_BilancioAllegato))) {
            return 'Specificare il tipo del 1° bilancio';
        }
    });

    my_TipoBilancioAbbr2II = createWarning(this.store, (myVerbale) => {
        const my_TipoBilancioAbbr2 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_TipoBilancioAbbr2;
        const my_BilancioAllegato = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_BilancioAllegato;

        if (!(!isNilOrEmpty(my_TipoBilancioAbbr2) || isNilOrEmpty(my_BilancioAllegato))) {
            return 'Specificare il tipo del 2° bilancio';
        }
    });

    my_StPaPsTOT1 = createWarning(this.store, (myVerbale) => {
        const my_StPaPsTOT1 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_StatoPatrimoniale__Passivo.my_StPaPsTOT1;
        const my_StPaAtTOT1 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_StatoPatrimoniale__Attivo.my_StPaAtTOT1;
        const my_DataApprovazione1 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_DataApprovazione1;
        const my_TipoBilancioAbbr1 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_TipoBilancioAbbr1;
        const my_BilancioAllegato = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_BilancioAllegato;

        if (!(isNilOrEmpty(my_BilancioAllegato) || my_StPaPsTOT1 === my_StPaAtTOT1 || isNilOrEmpty(my_DataApprovazione1) || my_TipoBilancioAbbr1 === '1')) {
            return 'Lo Stato Patr. (Esteso) del 1° bilancio è squadrato';
        }
    });

    my_StPaPsTOT1II = createWarning(this.store, (myVerbale) => {
        const my_StPaPsTOT1 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_StatoPatrimoniale__Passivo.my_StPaPsTOT1;
        const my_StPaAtTOT1 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_StatoPatrimoniale__Attivo.my_StPaAtTOT1;
        const my_DataApprovazione1 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_DataApprovazione1;
        const my_TipoBilancioAbbr1 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_TipoBilancioAbbr1;
        const my_BilancioAllegato = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_BilancioAllegato;

        if (!(isNilOrEmpty(my_BilancioAllegato) || my_StPaPsTOT1 === my_StPaAtTOT1 || isNilOrEmpty(my_DataApprovazione1) || my_TipoBilancioAbbr1 === '0')) {
            return 'Lo Stato Patr. (Breve) del 1° bilancio è squadrato';
        }
    });

    my_StPaPsTOT2 = createWarning(this.store, (myVerbale) => {
        const my_StPaPsTOT2 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_StatoPatrimoniale__Passivo.my_StPaPsTOT2;
        const my_StPaAtTOT2 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_StatoPatrimoniale__Attivo.my_StPaAtTOT2;
        const my_TipoBilancioAbbr2 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_TipoBilancioAbbr2;
        const my_BilancioAllegato = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_BilancioAllegato;
        if (!(isNilOrEmpty(my_BilancioAllegato) || my_StPaPsTOT2 === my_StPaAtTOT2 || my_TipoBilancioAbbr2 === '1')) {
            return 'Lo Stato Patr. (Esteso) del 2° bilancio è squadrato';
        }
    });

    my_StPaPsTOT2II = createWarning(this.store, (myVerbale) => {
        const my_StPaPsTOT2 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_StatoPatrimoniale__Passivo.my_StPaPsTOT2;
        const my_StPaAtTOT2 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_StatoPatrimoniale__Attivo.my_StPaAtTOT2;
        const my_TipoBilancioAbbr2 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_TipoBilancioAbbr2;
        const my_BilancioAllegato = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_BilancioAllegato;

        if (!(isNilOrEmpty(my_BilancioAllegato) || my_StPaPsTOT2 === my_StPaAtTOT2 || my_TipoBilancioAbbr2 === '0')) {
            return 'Lo Stato Patr. (Breve) del 2° bilancio è squadrato';
        }
    });

    my_StPaAtTOT1 = createWarning(this.store, (myVerbale) => {
        const my_BilancioAllegato = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_BilancioAllegato;
        const my_DataApprovazione1 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_DataApprovazione1;
        const my_TipoSchemaBil1 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_TipoSchemaBil1;
        const my_StPaAtTOT1 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_StatoPatrimoniale__Passivo.my_StPaPsAtot1;

        if (!(isNilOrEmpty(my_BilancioAllegato) || (isNilOrEmpty(my_DataApprovazione1) && isNilOrEmpty(my_TipoSchemaBil1)) || my_StPaAtTOT1 != '0')) {
            return 'Lo stato Patri. Attivo del 1° bilancio ha tot. nullo';
        }
    });

    my_StPaAtTOT2 = createWarning(this.store, (myVerbale) => {
        const my_BilancioAllegato = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_BilancioAllegato;
        const my_StPaAtTOT2 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_StatoPatrimoniale__Passivo.my_StPaPsAtot2;

        if (!(isNilOrEmpty(my_BilancioAllegato) || my_StPaAtTOT2 != '0')) {
            return 'Lo stato Patri. Attivo del 2° bilancio ha tot. nullo';
        }
    });

    my_CtrlDatiAggiuntiviBilBreve = createWarning(this.store, (myVerbale) => {
        const my_CtrlDatiAggiuntiviBilBreve = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_CtrlDatiAggiuntiviBilBreve;

        if (my_CtrlDatiAggiuntiviBilBreve != 'OK') {
            return 'Controllare i dati aggiuntivi del bilancio breve';
        }
    });

    my_DataApprovazione1II = createWarning(this.store, (myVerbale) => {
        const my_DataApprovazione1 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_DataApprovazione1;
        const my_TipoSchemaBil1 = myVerbale.my_SezioneI__Rilevazione.my_DatiBilancio.my_TipoSchemaBil1;
        const my_BilancioAllegato = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_BilancioAllegato;

        if (!(!isNilOrEmpty(my_DataApprovazione1) || !isNilOrEmpty(my_TipoSchemaBil1) || isNilOrEmpty(my_BilancioAllegato))) {
            return 'Il bilancio del 1° anno non è stato inserito';
        }
    });

    my_RegimeDerogatorioProvalenza = createWarning(this.store, (myVerbale) => {
        const my_RegimeDerogatorioProvalenza = myVerbale.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_RegimeDerogatorioProvalenza;
        const my_CkApplicatiRistorni = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_CkApplicatiRistorni;
        const my_CkApplicaRistorniAnno1 = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_CkApplicaRistorniAnno1;

        if (my_RegimeDerogatorioProvalenza !== '1' && (my_CkApplicatiRistorni === '1' || my_CkApplicaRistorniAnno1 === '1')) {
            return "Attenzione per il calcolo dell'avanzo di gestione generato da soci utilizzare la percentuale di prevalenza calcolata al netto degli impatti del regime derogatorio";
        }
    });

    my_CkDSuperioreB = createWarning(this.store, (myVerbale) => {
        const my_CkDSuperioreB = myVerbale.my_WarningsRistorni.my_CkDSuperioreB;
        const my_CkDSuperioreBAnno1 = myVerbale.my_WarningsRistorni.my_CkDSuperioreBAnno1;

        if (!(toNum(my_CkDSuperioreB) === 0 && toNum(my_CkDSuperioreBAnno1) === 0)) {
            return "Eccessivo ristorno per le cooperative di lavoro: il valore del punto D) non può essere superiore all' avanzo di gestione (B)";
        }
    });

    my_CkDSuperiore30PerCentoC = createWarning(this.store, (myVerbale) => {
        const my_CkDSuperiore30PerCentoC = myVerbale.my_WarningsRistorni.my_CkDSuperiore30PerCentoC;
        const my_CkDSuperiore30PerCentoCAnno1 = myVerbale.my_WarningsRistorni.my_CkDSuperiore30PerCentoCAnno1;

        if (!(toNum(my_CkDSuperiore30PerCentoC) === 0 && toNum(my_CkDSuperiore30PerCentoCAnno1) === 0)) {
            return 'Eccessivo ristorno per le cooperative di lavoro: il valore del punto D) non può essere superiore al 30% del punto C)';
        }
    });

    my_CkDSuperioreCEB9 = createWarning(this.store, (myVerbale) => {
        const my_CkDSuperioreCEB9 = myVerbale.my_WarningsRistorni.my_CkDSuperioreCEB9;
        const my_CkDSuperioreCEB9Anno1 = myVerbale.my_WarningsRistorni.my_CkDSuperioreCEB9Anno1;

        if (!(toNum(my_CkDSuperioreCEB9) === 0 && toNum(my_CkDSuperioreCEB9Anno1) === 0)) {
            return 'Eccessivo ristorno per le cooperative di lavoro: il valore del punto D) non può essere superiore al costo del personale (CE B9)';
        }
    });

    my_CkESuperioreB = createWarning(this.store, (myVerbale) => {
        const my_CkESuperioreB = myVerbale.my_WarningsRistorni.my_CkESuperioreB;
        const my_CkESuperioreBAnno1 = myVerbale.my_WarningsRistorni.my_CkESuperioreBAnno1;

        if (!(toNum(my_CkESuperioreB) === 0 && toNum(my_CkESuperioreBAnno1) === 0)) {
            return "Eccessivo ristorno per le cooperative di utenza: il valore del punto E) non può essere superiore all' avanzo di gestione (B)";
        }
    });

    my_CkFSuperioreB = createWarning(this.store, (myVerbale) => {
        const my_CkFSuperioreB = myVerbale.my_WarningsRistorni.my_CkFSuperioreB;
        const my_CkFSuperioreBAnno1 = myVerbale.my_WarningsRistorni.my_CkFSuperioreBAnno1;

        if (!(toNum(my_CkFSuperioreB) === 0 && toNum(my_CkFSuperioreBAnno1) === 0)) {
            return "Eccessivo ristorno per le cooperative di apporto: il valore del punto F) non può essere superiore all' avanzo di gestione (B)";
        }
    });

    my_CkGSuperioreB = createWarning(this.store, (myVerbale) => {
        const my_CkGSuperioreB = myVerbale.my_WarningsRistorni.my_CkGSuperioreB;
        const my_CkGSuperioreBAnno1 = myVerbale.my_WarningsRistorni.my_CkGSuperioreBAnno1;

        if (!(toNum(my_CkGSuperioreB) === 0 && toNum(my_CkGSuperioreBAnno1) === 0)) {
            return "L'importo della liquidazione salariale o del prezzo o del servizio (G) non può essere superiore all'avanzo di gestione (B)";
        }
    });

    my_CkHSuperioreB = createWarning(this.store, (myVerbale) => {
        const my_CkHSuperioreB = myVerbale.my_WarningsRistorni.my_CkHSuperioreB;
        const my_CkHSuperioreBAnno1 = myVerbale.my_WarningsRistorni.my_CkHSuperioreBAnno1;

        if (!(toNum(my_CkHSuperioreB) === 0 && toNum(my_CkHSuperioreBAnno1) === 0)) {
            return "L'aumento gratuito del Capitale Sociale (H) non può essere superiore all' avanzo di gestione (B)";
        }
    });

    my_CkISuperioreB = createWarning(this.store, (myVerbale) => {
        const my_CkISuperioreB = myVerbale.my_WarningsRistorni.my_CkISuperioreB;
        const my_CkISuperioreBAnno1 = myVerbale.my_WarningsRistorni.my_CkISuperioreBAnno1;

        if (!(toNum(my_CkISuperioreB) === 0 && toNum(my_CkISuperioreBAnno1) === 0)) {
            return "La distribuzione gratuita di titoli (I) non può essere superiore all' avanzo di gestione (B)";
        }
    });

    my_CkGHISuperioriB = createWarning(this.store, (myVerbale) => {
        const my_CkGHISuperioriB = myVerbale.my_WarningsRistorni.my_CkGHISuperioriB;
        const my_CkGHISuperioriBAnno1 = myVerbale.my_WarningsRistorni.my_CkGHISuperioriBAnno1;

        if (!(toNum(my_CkGHISuperioriB) === 0 && toNum(my_CkGHISuperioriBAnno1) === 0)) {
            return "La somma delle distribuzioni, dell'aumento di capitale e della distribuzione non può essere superiore all'avanzo di gestione (B)";
        }
    });

    my_CkPrestitoSoci = createWarning(this.store, (myVerbale) => {
        const my_CkPrestitoSoci = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_CkPrestitoSoci;
        const my_StPaAtAtot2 = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_CapSocSottoscrSecondoEs;
        const my_NumBilanciInExcel = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_NumBilanciInExcel;
        const my_CapSocVersatoSecondoEs = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_CapSocVersatoSecondoEs;
        const my_CapSocSottoscrSecondoEs = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_CapSocSottoscrSecondoEs;

        if (
            !(
                my_CkPrestitoSoci != '1' ||
                (my_CkPrestitoSoci === '1' && toNum(my_StPaAtAtot2) <= 0) ||
                isNilOrEmpty(my_NumBilanciInExcel) ||
                toNum(my_NumBilanciInExcel) < 1 ||
                isNilOrEmpty(my_CapSocVersatoSecondoEs) ||
                isNilOrEmpty(my_CapSocSottoscrSecondoEs)
            )
        ) {
            return 'Attenzione: verificare i termini di versamento del capitale sottoscritto da parte dei soci';
        }
    });

    my_CkObbligoBilSoc = createWarning(this.store, (myVerbale) => {
        const my_CkObbligoBilSoc = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_CkObbligoBilSoc;
        const my_CkRedazBilSociale = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_CkRedazBilSociale;

        if (!(my_CkObbligoBilSoc != '1' || my_CkRedazBilSociale != '0')) {
            return "La cooperativa è tenuta a redigere il Bilancio Sociale, non sarà possibile rilasciare l'attestato di revisione";
        }
    });

    my_RagSocRevIncaricataChoice = createAsyncWarning(this.store, (myVerbale) => {
        const ragSoc = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_RagSocRevIncaricataChoice;

        return this.store.select(Revisione53DatiBusinessSelectors.getSocietaRevisione(ragSoc)).pipe(
            take(1),
            filter((codiceRagSoc) => !!codiceRagSoc),
            map(() => {
                const my_ElCertifPassate = myVerbale.my_CertificazioniPassate.my_ElCertificazioniPassate;
                let totUguali = 0;
                let nextYear = null;
                let ultimoAnno = 0;
                let ultimaSocieta = 0;

                my_ElCertifPassate.forEach((element) => {
                    const idSocCert = element.my_CertPassataIdSocieta;
                    const annoCertAppNode = element.my_CertPassataAnnoBil;

                    if (!isNilOrEmpty(idSocCert) && !isNilOrEmpty(annoCertAppNode)) {
                        nextYear = ultimoAnno + 1;
                        if (nextYear != toNum(annoCertAppNode) || ultimaSocieta != toNum(idSocCert)) {
                            totUguali = 1;
                        } else {
                            totUguali++;
                        }
                        ultimoAnno = toNum(annoCertAppNode);
                        ultimaSocieta = toNum(idSocCert);
                    }
                });

                const annoBilRevisionato = myVerbale.my_SezioneI__Rilevazione.my_Bilancio.my_AnnoSecondoEs;
                nextYear = ultimoAnno + 1;

                if (ultimaSocieta === toNum(ragSoc) && nextYear === toNum(annoBilRevisionato)) {
                    totUguali++;
                }

                if (totUguali >= 7) {
                    return 'Attenzione: per 7 anni consecutivi si usa la stessa società di certificazione';
                }

                return undefined;
            })
        );
    });
}
